import React, { createRef, useMemo, useRef, useState } from 'react';
import { TextVariant } from '@naf/text';
import { PortableText } from '@portabletext/react';
import useSelector from '../../../redux/typedHooks';
import { ProsAndCons } from '../../../components/bilguide/ProsAndCons';
import { HeaderText } from '../HeaderText';
import { ModelImageCarousel } from '../ModelImageCarousel';
import { SafetyTest } from '../../../components/bilguide/carTest/SafetyTest';
import { RangeTests } from '../RangeTests';
import { ChargeTests } from '../ChargeTests';
import { MotorTest } from '../MotorTest';
import { TestChips } from '../TestChips';
import { SectionsNavigation } from '../SectionsNavigation';
import { VehicleData } from '../VehicleData';
import { NeighbouringModelNav } from '../NeighbouringModelNav';
import { TrunkTests } from '../TrunkTests';
import { ColumnBlock } from '../../../components/SectionBlocks/ColumnBlock';
import RelatedArticleList from '../../../components/related-articles/RelatedArticleList';
import FeedbackSurvey from '../../../components/feedbacksurvey/FeedbackSurvey';
import { useWindowLocation } from '../../../hooks/Window/useWindowLocation';
import { RelatedModels } from '../RelatedModels';
import { BlockContentWrapper } from '../BlockContentWrapper';
import { LoaderContent } from '../../LoaderPage';
import { Season } from '../../../../../types/ElPrixContextData';
import { Props, SectionKey } from './Types';
import { ScrollComponent } from './ScrollComponent';
import * as S from './Styles';

const ModelDetailPage = ({ slug }: Props) => {
  const path = useWindowLocation();
  const stateData = useSelector((state) => state.cars.mappedData[slug]);
  const moreContent = stateData?.data?.meta?.globalContent?.moreContent || [];
  const [currentComponentInView, setComponentInView] = useState<SectionKey>('about');

  const sectionRefs = useRef<Record<SectionKey, React.RefObject<HTMLDivElement>>>({
    model: createRef(),
    about: createRef(),
    rangeTest: createRef(),
    chargeTest: createRef(),
    trunkTest: createRef(),
    safetyTest: createRef(),
    other: createRef(),
  });

  const scrollTo = (sectionKey: SectionKey) => {
    const ref = sectionRefs.current[sectionKey];
    if (ref?.current) {
      ref.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const scrollToMotorTest = () => {
    const motorTestElement = document.getElementById('motorTest');
    if (motorTestElement) {
      motorTestElement.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleTestChipClick = (testDate: string, season: Season) => {
    scrollTo('rangeTest');
    window.dispatchEvent(
      new CustomEvent('selectRangeTest', {
        detail: { testDate, season },
      }),
    );
  };

  const sectionMetadata = useMemo(() => {
    const { meta, safetyTest } = stateData?.data || {};
    if (!meta) return [];

    return [
      { key: 'model', label: meta.name, isPresent: true },
      { key: 'about', label: 'Generelt om modellen', isPresent: true },
      { key: 'rangeTest', label: 'Test av rekkevidde', isPresent: meta.rangeTests?.length > 0 },
      { key: 'chargeTest', label: 'Test av lading', isPresent: meta.chargeTests?.length > 0 },
      { key: 'trunkTest', label: 'Test av bagasjerom', isPresent: meta.trunkTests?.length > 0 },
      { key: 'safetyTest', label: 'Bilens sikkerhet', isPresent: !!safetyTest },
      { key: 'other', label: 'Andre tester, tips og råd', isPresent: meta.relatedContent?.length > 0 },
    ].filter((s) => s.isPresent);
  }, [stateData?.data]);

  const renderSectionContent = (key: SectionKey) => {
    const { meta, motorTests, safetyTest } = stateData?.data || {};

    switch (key) {
      case 'model':
        return (
          <>
            <HeaderText categories={meta.categories} title={meta.name} ingress={meta.ingress} />
            <TestChips
              rangeTests={meta.rangeTests}
              chargeTests={meta.chargeTests}
              testedByMotor={motorTests?.length > 0}
              onNafTestClick={handleTestChipClick}
              onMotorTestClick={scrollToMotorTest}
            />
            <ModelImageCarousel sanityImages={meta.cloudinaryImages} />
          </>
        );
      case 'about':
        return (
          <>
            <VehicleData
              onClick={scrollTo}
              data={{ ...stateData.data, chargeTests: meta.chargeTests, rangeTests: meta.rangeTests }}
            />
            {meta.body && (
              <BlockContentWrapper>
                <PortableText value={meta.body} />
              </BlockContentWrapper>
            )}
            <ProsAndCons pros={meta.prosAndCons?.pros} cons={meta.prosAndCons?.cons} />
            <MotorTest motorTest={motorTests?.[0]} />
          </>
        );
      case 'rangeTest':
        return (
          <RangeTests
            slug={slug}
            rangeTestInfoBox={meta.globalContent?.rangeTestInfoBox}
            rangeTests={meta.rangeTests}
            cloudinaryImages={meta.cloudinaryImages}
            chargeTests={meta.chargeTests}
          />
        );
      case 'chargeTest':
        return (
          <ChargeTests
            slug={slug}
            chargeTestInfoBox={meta.globalContent?.chargeTestInfoBox}
            chargeTests={meta.chargeTests}
            cloudinaryImages={meta.cloudinaryImages}
            rangeTests={meta.rangeTests}
          />
        );
      case 'trunkTest':
        return <TrunkTests slug={slug} trunkTests={meta.trunkTests} />;
      case 'safetyTest':
        return (
          <>
            <S.SectionHeader variant={TextVariant.Header1} tag="h2">
              Bilens sikkerhet
            </S.SectionHeader>
            <SafetyTest safetyTest={safetyTest} />
          </>
        );
      case 'other':
        return (
          <S.RelatedContentWrapper>
            <S.SectionHeader variant={TextVariant.Header1} tag="h2">
              Andre tester, tips og råd
            </S.SectionHeader>
            <RelatedArticleList relatedContent={meta.relatedContent} />
          </S.RelatedContentWrapper>
        );
      default:
        return null;
    }
  };

  if (!stateData?.data?.meta) {
    return <LoaderContent />;
  }

  return (
    <S.Container>
      <S.Main>
        {sectionMetadata.map(({ key }, i) => (
          <ScrollComponent
            key={key}
            index={i}
            fireOnInView={() => setComponentInView(key as SectionKey)}
            observerRef={sectionRefs.current[key]}
          >
            {renderSectionContent(key as SectionKey)}
          </ScrollComponent>
        ))}
        <S.Section $isBottomElement>
          <S.SectionContent $unset>
            <NeighbouringModelNav currentSlug={slug} />
          </S.SectionContent>
        </S.Section>
        <S.Section $isBottomElement>
          <S.SectionContent $unset $relative>
            <RelatedModels currentSlug={slug} inSidebar={false} />
          </S.SectionContent>
        </S.Section>
        <S.Section>
          <S.SectionContent>
            {moreContent.map((block, i) => (
              <ColumnBlock
                key={block.title || i}
                headerVariant="large"
                block={block}
                activateContextThemeSwitch={false}
              />
            ))}
            {moreContent.length > 0 && <S.FeedbackMargin />}
            <FeedbackSurvey
              title="Hvordan var innholdet på denne siden?"
              customText={{
                positiveRadioLabel: 'Bra',
                negativeRadioLabel: 'Dårlig',
                positiveTextAreaPlaceholder: 'Så bra! Er det noe vi kunne ha gjort enda bedre?',
                negativeTextAreaPlaceholder: 'Det var dumt. Kan du fortelle hva du savnet eller håpet hadde vært her?',
              }}
              contentUrl={path}
            />
          </S.SectionContent>
        </S.Section>
      </S.Main>
      <S.Aside>
        <SectionsNavigation
          slug={slug}
          modelName={stateData?.data?.meta?.name}
          navigateToSection={scrollTo}
          sections={sectionMetadata.map(({ key, label }) => ({
            key: key as SectionKey,
            label,
            ref: sectionRefs.current[key],
          }))}
          sectionInView={currentComponentInView}
        />
      </S.Aside>
    </S.Container>
  );
};

export { ModelDetailPage };
